import React, { useState, useEffect } from "react";
import { CheckCircle, XCircle, Trash2, FileText } from "lucide-react";
import {
  LEAVE_STATUS_COLORS, LEAVE_STATUS_LABELS,
  LEAVE_TYPES,
} from "../../constants/hrConstants";
import { DataTable } from "@/components/ui/DataTable";
import { Pagination } from "@/components/ui/Pagination";

const typeLabel = (v) => LEAVE_TYPES.find((t) => t.value === v)?.label || v;

export function LeaveRequestsTable({ leaves, employees, onApprove, onReject, onCancel, onDelete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Reset to first page when filtering results in fewer pages
  useEffect(() => {
    setCurrentPage(1);
  }, [leaves.length]);

  if (!leaves.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
        <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium text-[13px]">No leave requests found</p>
        <p className="text-slate-400 text-[12px] mt-1">Requests will appear here</p>
      </div>
    );
  }

  const totalPages = Math.ceil(leaves.length / pageSize) || 1;
  const paginatedLeaves = leaves.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const columns = [
    {
      header: "Employee",
      key: "employeeName",
      render: (l) => {
        const emp = employees?.find(e => e.id === l.employeeId);
        return (
          <div className="overflow-hidden">
            <p className="text-sm font-bold text-slate-800 truncate" title={l.employeeName}>
              {l.employeeName}
            </p>
            <p className="text-xs font-medium text-slate-400 mt-0.5 truncate">
              {emp?.phone || `#${l.employeeId}`}
            </p>
          </div>
        );
      }
    },
    {
      header: "Leave Type",
      key: "leaveType",
      render: (l) => (
        <div className="overflow-hidden">
          <p className="text-sm text-slate-700 truncate font-semibold">{typeLabel(l.leaveType)}</p>
          {l.durationType === "half_day" && (
            <p className="text-xs text-slate-500 capitalize mt-0.5">{l.durationType.replace("_", " ")}</p>
          )}
        </div>
      )
    },
    {
      header: "From",
      key: "fromDate",
      render: (l) => (
        <span className="text-sm font-medium text-slate-600 truncate">
          {new Date(l.fromDate).toLocaleDateString("en-IN")}
        </span>
      )
    },
    {
      header: "To",
      key: "toDate",
      render: (l) => (
        <span className="text-sm font-medium text-slate-600 truncate">
          {l.durationType === "half_day" 
            ? "—" 
            : new Date(l.toDate).toLocaleDateString("en-IN")}
        </span>
      )
    },
    {
      header: "Days",
      key: "days",
      className: "text-center",
      render: (l) => (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
          {l.days}d
        </span>
      )
    },
    {
      header: "Reason",
      key: "reason",
      render: (l) => (
        <span className="text-xs text-slate-500 truncate block max-w-[150px]" title={l.reason}>
          {l.reason}
        </span>
      )
    },
    {
      header: "Status",
      key: "status",
      render: (l) => (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${LEAVE_STATUS_COLORS[l.status] || ""}`}>
          {LEAVE_STATUS_LABELS[l.status] || l.status}
        </span>
      )
    },
    {
      header: "Applied On",
      key: "appliedOn",
      render: (l) => (
        <span className="text-xs font-medium text-slate-500 truncate">
          {new Date(l.appliedOn).toLocaleDateString("en-IN")}
        </span>
      )
    },
    {
      header: "Actions",
      key: "actions",
      className: "text-right pr-4",
      render: (l) => (
        <div className="flex items-center gap-1.5 justify-end">
          {l.status === "pending" && (
            <>
              <button
                onClick={() => onApprove(l.id)}
                className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                title="Approve Request"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => onReject(l.id)}
                className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors"
                title="Reject Request"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
          {l.status === "approved" && (
            <button
              onClick={() => onCancel(l.id)}
              className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-amber-600 hover:bg-amber-50 hover:border-amber-200 transition-colors"
              title="Cancel Approved Leave"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onDelete(l.id)}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors"
            title="Delete Record"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm flex flex-col">
      <DataTable columns={columns} data={paginatedLeaves} tableClassName="min-w-[950px]" />
      {totalPages > 1 && (
        <Pagination 
          page={currentPage} 
          totalPages={totalPages} 
          onPageChange={setCurrentPage} 
        />
      )}
    </div>
  );
}
